import {Close, DragIndicator, ExpandMore} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import React, {useMemo} from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {i18n} from 'src/i18n';
import {
  DEFAULT_PARENT_FIELD_CELLS_PER_ROW,
  DEFAULT_PARENT_FIELD_COL_SPAN,
  PARENT_FIELD_CELLS_PER_ROW_OPTIONS,
  ParentFieldLayoutConfig,
  resolveParentFieldsForRender,
} from 'src/modules/tarea/parentFieldLayout';

export type ParentAnswersPanelProps = {
  questions: Array<{key: string; label: string; answer?: string}>;
  layoutConfig: ParentFieldLayoutConfig;
  onToggle: (key: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onColSpanChange: (key: string, colSpan: number) => void;
  onCellsPerRowChange: (cellsPerRow: number) => void;
  savingConfig?: boolean;
  /** false mientras se resuelve modelo/config — evita toggles prematuros que bloquean el hydrate. */
  configReady?: boolean;
};

function ParentAnswersPanel({
  questions,
  layoutConfig,
  onToggle,
  onReorder,
  onColSpanChange,
  onCellsPerRowChange,
  savingConfig,
  configReady = true,
}: ParentAnswersPanelProps) {
  const selectedFields = layoutConfig.fields;
  const cellsPerRow = layoutConfig.cellsPerRow || DEFAULT_PARENT_FIELD_CELLS_PER_ROW;
  const selectedKeys = useMemo(() => new Set(selectedFields.map((field) => field.key)), [selectedFields]);
  const labelByKey = useMemo(
    () => new Map(questions.map((question) => [question.key, question.label])),
    [questions],
  );
  const previewFields = useMemo(
    () =>
      resolveParentFieldsForRender(questions, selectedFields, {
        cellsPerRow,
        defaultColSpan: DEFAULT_PARENT_FIELD_COL_SPAN,
      }),
    [questions, selectedFields, cellsPerRow],
  );
  const interactionsDisabled = !configReady;

  const handleDragEnd = (result: any) => {
    if (interactionsDisabled) return;
    if (!result.destination || result.destination.index === result.source.index) return;
    onReorder(result.source.index, result.destination.index);
  };

  return (
    <Box mb={2} gap={1} display="flex" flexDirection="column">
      <Accordion defaultExpanded={false} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="body2">Campos seleccionados</Typography>
            <Chip size="small" label={selectedFields.length} />
            {!configReady && <CircularProgress size={14} />}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {questions.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {i18n('entities.tarea.parentAnswersEmpty')}
            </Typography>
          ) : (
            <>
              <Box
                display="flex"
                gap={0.75}
                flexWrap="wrap"
                mb={1.5}
                sx={{opacity: interactionsDisabled ? 0.55 : 1, pointerEvents: interactionsDisabled ? 'none' : 'auto'}}>
                {questions.map((question) => {
                  const selected = selectedKeys.has(question.key);
                  return (
                    <Chip
                      key={question.key}
                      size="small"
                      label={question.label}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => onToggle(question.key)}
                      disabled={interactionsDisabled}
                    />
                  );
                })}
              </Box>

              {selectedFields.length > 0 && (
                <Box
                  sx={{opacity: interactionsDisabled ? 0.55 : 1, pointerEvents: interactionsDisabled ? 'none' : 'auto'}}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                    <Typography variant="caption" color="textSecondary">
                      {i18n('entities.tarea.parentFieldsActive')}
                    </Typography>
                    <FormControl size="small" sx={{minWidth: 140, ml: 'auto'}} disabled={interactionsDisabled}>
                      <InputLabel id="parent-field-cells-per-row-label">
                        {i18n('entities.tarea.parentFieldCellsPerRow')}
                      </InputLabel>
                      <Select
                        labelId="parent-field-cells-per-row-label"
                        label={i18n('entities.tarea.parentFieldCellsPerRow')}
                        value={cellsPerRow}
                        onChange={(event) => onCellsPerRowChange(Number(event.target.value))}>
                        {PARENT_FIELD_CELLS_PER_ROW_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {savingConfig && (
                      <Chip size="small" variant="outlined" label={i18n('entities.tarea.savingTableConfig')} />
                    )}
                  </Box>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="parent-field-layout" isDropDisabled={interactionsDisabled}>
                      {(provided) => (
                        <Box ref={provided.innerRef} {...provided.droppableProps}>
                          {selectedFields.map((field, index) => (
                            <Draggable
                              key={field.key}
                              draggableId={field.key}
                              index={index}
                              isDragDisabled={interactionsDisabled}>
                              {(dragProvided, snapshot) => (
                                <Paper
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  variant="outlined"
                                  sx={{
                                    mb: 0.75,
                                    px: 1,
                                    py: 0.75,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                                  }}>
                                  <Box
                                    {...dragProvided.dragHandleProps}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      cursor: 'grab',
                                      color: 'text.secondary',
                                    }}>
                                    <DragIndicator fontSize="small" />
                                  </Box>
                                  <Typography variant="body2" sx={{flex: 1, minWidth: 0}} noWrap>
                                    {labelByKey.get(field.key) || field.key}
                                  </Typography>
                                  <FormControl size="small" sx={{minWidth: 72}} disabled={interactionsDisabled}>
                                    <Select
                                      value={Math.min(field.colSpan, cellsPerRow)}
                                      onChange={(event) => onColSpanChange(field.key, Number(event.target.value))}
                                      inputProps={{
                                        'aria-label': i18n('entities.tarea.parentFieldColSpan'),
                                      }}>
                                      {Array.from({length: cellsPerRow}, (_, index) => index + 1).map((value) => (
                                        <MenuItem key={value} value={value}>
                                          {value}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <IconButton
                                    size="small"
                                    onClick={() => onToggle(field.key)}
                                    aria-label="remove"
                                    disabled={interactionsDisabled}>
                                    <Close fontSize="small" />
                                  </IconButton>
                                </Paper>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <Card variant="outlined">
        <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
          {questions.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {i18n('entities.tarea.parentAnswersEmpty')}
            </Typography>
          ) : previewFields.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              {i18n('entities.tarea.parentAnswersSelectAtLeastOne')}
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cellsPerRow}, minmax(0, 1fr))`,
                gap: 1.25,
              }}>
              {previewFields.map((question) => (
                <Paper
                  key={question.key}
                  variant="outlined"
                  sx={{
                    p: 1,
                    gridColumn: `span ${Math.min(question.colSpan, cellsPerRow)}`,
                    minWidth: 0,
                  }}>
                  <Typography variant="caption" color="textSecondary">
                    {question.label}
                  </Typography>
                  <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>
                    {question.answer || '-'}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default ParentAnswersPanel;
